import cn from 'classnames';
import {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type UIEvent,
} from 'react';
import { useIntl } from 'react-intl';

import {
  archiveAgentCoreThread,
  type AgentCoreActor,
  type AgentCoreThreadSummary,
  type AgentThreadSubject,
  listAgentCoreThreads,
} from '@/shared/api/agentCore/agentCoreApi';
import { useAppSelector } from '@/shared/hooks';
import { readChatThreadTitles, writeChatThreadTitles } from '@/shared/lib';
import { Icon, Spinner } from '@/shared/ui';

import { organizationModel } from '@/entities/organization';
import { userModel } from '@/entities/user';

import { chatThreadListMessages } from '../config';

import styles from './ChatThreadList.module.scss';

// Calendar-day comparison, not a rolling 24h window: a thread updated
// yesterday 23:30 must group under "Yesterday", not "Today".
const isSameCalendarDay = (date: Date, reference: Date): boolean =>
  date.getFullYear() === reference.getFullYear() &&
  date.getMonth() === reference.getMonth() &&
  date.getDate() === reference.getDate();

type ChatThreadListProps = {
  patientID: string;
  className?: string;
  patientName?: string;
  showSearch?: boolean;
  threadID?: string;
  /** Current page's entity: its chats render above the patient's others. */
  subject?: AgentThreadSubject | null;
  onActiveThreadTitle?: (title: string) => void;
  onThreadOpen?: (threadID: string) => void;
};

export const ChatThreadList: FC<ChatThreadListProps> = ({
  className,
  threadID,
  onActiveThreadTitle,
  onThreadOpen,
  showSearch = true,
}) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const currentUser = useAppSelector(userModel.selectors.selectCurrentUser);
  const organizationID = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationID,
  );
  const [search, setSearch] = useState('');
  const [menuThreadID, setMenuThreadID] = useState('');
  // Any click outside an open thread menu closes it.
  useEffect(() => {
    if (!menuThreadID) {
      return undefined;
    }
    const close = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      if (!target.closest('[data-thread-menu]')) {
        setMenuThreadID('');
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuThreadID]);
  // Track which titles actually overflow their row so the gradient
  // shade-in applies only to truncated text.
  const [overflowing, setOverflowing] = useState<Record<string, boolean>>({});
  const titleRefs = useRef<Record<string, HTMLSpanElement | null>>({});
  const [renamingThreadID, setRenamingThreadID] = useState('');
  // True once the current rename editor committed (submit) or was cancelled
  // (Escape): the unmount blur that follows must not commit again.
  const renameCommittedRef = useRef(false);
  const [renameValue, setRenameValue] = useState('');
  const [threads, setThreads] = useState<AgentCoreThreadSummary[]>([]);
  const scrollIdleTimersRef = useRef(
    new Map<HTMLElement, ReturnType<typeof setTimeout>>(),
  );

  // Show the thread list scrollbar (thumb only) while scrolling; CSS `:hover`
  // covers the resting-inside case.
  const handleListScroll = useCallback((event: UIEvent<HTMLElement>) => {
    const el = event.target;
    if (!(el instanceof HTMLElement)) {
      return;
    }
    el.dataset.scrolling = 'true';
    clearTimeout(scrollIdleTimersRef.current.get(el));
    scrollIdleTimersRef.current.set(
      el,
      setTimeout(() => {
        delete el.dataset.scrolling;
        scrollIdleTimersRef.current.delete(el);
      }, 650),
    );
  }, []);

  useEffect(
    () => () => {
      scrollIdleTimersRef.current.forEach(clearTimeout);
      scrollIdleTimersRef.current.clear();
    },
    [],
  );

  const [isLoadingThreads, setIsLoadingThreads] = useState(true);
  // 'load' — the list request failed; 'archive' — the last archive call
  // failed. The stale list stays visible either way, with a retry.
  const [listError, setListError] = useState<'load' | 'archive' | null>(null);

  const loadThreads = useCallback(async () => {
    setIsLoadingThreads(true);
    setListError(null);
    try {
      const actor: AgentCoreActor = { userID: currentUser.ID };
      // Patient threads are org-visible; the general call returns only the
      // caller's own (server enforces creator privacy).
      const items = await listAgentCoreThreads(actor, {
        organization_id: organizationID,
      });
      setThreads(items.filter((item) => item.status !== 'archived'));
    } catch {
      // Keep the last known list; the error row below offers a retry.
      setListError('load');
    } finally {
      setIsLoadingThreads(false);
    }
  }, [currentUser.ID, organizationID]);

  useEffect(() => {
    void loadThreads();
  }, [loadThreads]);

  const knownThreadIDsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    knownThreadIDsRef.current = new Set(threads.map((item) => item.id));
  }, [threads]);
  // A run can adopt a server-created thread the list has never seen (the
  // list only reloads on mount and scope change): when the active thread
  // is unknown to the list, reload so it appears without a remount. The
  // first invocation is skipped — mount already loads, and the pointer
  // restored thread is normally in that first load.
  const skipUnknownCheckRef = useRef(true);
  useEffect(() => {
    if (skipUnknownCheckRef.current) {
      skipUnknownCheckRef.current = false;
      return;
    }
    if (!threadID || knownThreadIDsRef.current.has(threadID)) {
      return;
    }
    void loadThreads();
  }, [threadID, loadThreads]);

  // Renaming is a client-side overlay until the agent protocol grows a
  // rename RPC: overrides survive reloads in localStorage.
  const [titleOverrides, setTitleOverrides] =
    useState<Record<string, string>>(readChatThreadTitles);

  const applyRename = (threadID: string, title: string) => {
    const next = { ...titleOverrides, [threadID]: title };
    setTitleOverrides(next);
    writeChatThreadTitles(next);
    setThreads((current) =>
      current.map((item) => (item.id === threadID ? { ...item, title } : item)),
    );
  };

  const measureOverflow = useCallback(() => {
    const next: Record<string, boolean> = {};
    Object.entries(titleRefs.current).forEach(([id, el]) => {
      if (el) {
        next[id] = el.scrollWidth > el.clientWidth + 1;
      }
    });
    setOverflowing(next);
  }, []);

  useLayoutEffect(() => {
    measureOverflow();
  }, [threads, titleOverrides, measureOverflow]);

  useEffect(() => {
    window.addEventListener('resize', measureOverflow);
    return () => window.removeEventListener('resize', measureOverflow);
  }, [measureOverflow]);

  useEffect(() => {
    const active = threads.find((item) => item.id === threadID);
    const raw = active?.title || '';
    onActiveThreadTitle?.(
      titleOverrides[threadID ?? ''] ||
        raw ||
        formatMessage(chatThreadListMessages.newChat),
    );
  }, [threadID, threads, titleOverrides, onActiveThreadTitle]);

  const archiveThread = async (thread: AgentCoreThreadSummary) => {
    setMenuThreadID('');
    try {
      await archiveAgentCoreThread(
        { userID: currentUser.ID },
        { organization_id: organizationID },
        thread.id,
        true,
      );
      await loadThreads();
      if (thread.id === threadID) {
        // The chat overlay is not routed: switching away from the archived
        // thread must go through the runtime mirror, not navigation —
        // /patients/:id/agent matches no real route.
        onThreadOpen?.('');
      }
    } catch {
      setListError('archive');
    }
  };

  const query = search.trim().toLocaleLowerCase();
  const visible = threads.filter(
    (item) =>
      !query ||
      item.title.toLocaleLowerCase().includes(query) ||
      item.lastMessagePreview.toLocaleLowerCase().includes(query),
  );
  const byDateGroups = (
    items: AgentCoreThreadSummary[],
  ): { label: string; items: AgentCoreThreadSummary[] }[] =>
    items.reduce<{ label: string; items: AgentCoreThreadSummary[] }[]>(
      (acc, item) => {
        const date = new Date(item.updatedAt);
        const now = new Date();
        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);
        const label = isSameCalendarDay(date, now)
          ? formatMessage(chatThreadListMessages.today)
          : isSameCalendarDay(date, yesterday)
            ? formatMessage(chatThreadListMessages.yesterday)
            : intl.formatDate(date, {
                month: 'short',
                day: 'numeric',
              });
        const last = acc[acc.length - 1];
        if (last && last.label === label) {
          last.items.push(item);
        } else {
          acc.push({ label, items: [item] });
        }
        return acc;
      },
      [],
    );
  const dateGroups = byDateGroups(visible);

  const renderRow = (thread: AgentCoreThreadSummary) => {
    const title = titleOverrides[thread.id] || thread.title || thread.id;
    const isRenaming = renamingThreadID === thread.id;
    return (
      <div
        key={thread.id}
        className={cn(
          styles.threadRow,
          thread.id === threadID && styles.threadRowActive,
        )}
        data-thread-menu={menuThreadID === thread.id ? '' : undefined}
      >
        {isRenaming ? (
          <form
            className={styles.threadRenameForm}
            onSubmit={(event) => {
              event.preventDefault();
              renameCommittedRef.current = true;
              applyRename(thread.id, renameValue.trim() || title);
              setRenamingThreadID('');
            }}
          >
            <input
              autoFocus
              value={renameValue}
              className={styles.threadRenameInput}
              aria-label={formatMessage(chatThreadListMessages.rename)}
              onChange={(event) => setRenameValue(event.target.value)}
              onBlur={() => {
                // Enter already committed via form submit; Escape cancels.
                // Some browsers deliver blur while the cancelled editor
                // unmounts — without this guard that blur would commit the
                // rename (or commit it a second time) anyway.
                if (!renameCommittedRef.current) {
                  applyRename(thread.id, renameValue.trim() || title);
                }
                renameCommittedRef.current = false;
                setRenamingThreadID('');
              }}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  renameCommittedRef.current = true;
                  setRenamingThreadID('');
                }
              }}
            />
          </form>
        ) : (
          <>
            <button
              type="button"
              className={styles.threadRowButton}
              onClick={() => onThreadOpen?.(thread.id)}
            >
              <span
                ref={(el) => {
                  titleRefs.current[thread.id] = el;
                }}
                className={cn(
                  styles.threadRowTitle,
                  overflowing[thread.id] && styles.threadRowTitleTruncated,
                )}
              >
                {title}
              </span>
            </button>
            <button
              type="button"
              className={styles.threadRowMenu}
              aria-label={formatMessage(chatThreadListMessages.actions)}
              onClick={() =>
                setMenuThreadID((current) =>
                  current === thread.id ? '' : thread.id,
                )
              }
            >
              <Icon name="dots" size={16} />
            </button>
            {menuThreadID === thread.id ? (
              <div
                className={styles.threadRowMenuPopup}
                role="menu"
                aria-label={formatMessage(chatThreadListMessages.actions)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    setMenuThreadID('');
                  }
                }}
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setRenamingThreadID(thread.id);
                    setRenameValue(title);
                    setMenuThreadID('');
                  }}
                >
                  {formatMessage(chatThreadListMessages.rename)}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => void archiveThread(thread)}
                >
                  {formatMessage(chatThreadListMessages.archive)}
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>
    );
  };

  return (
    <div
      className={cn(styles.chatThreadList, className)}
      data-component="chat-thread-list"
    >
      {showSearch ? (
        <label className={styles.searchBox}>
          <Icon name="search" size={14} />
          <input
            value={search}
            placeholder={formatMessage(chatThreadListMessages.search)}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
      ) : null}
      <div className={styles.threadGroup}>
        {isLoadingThreads ? <Spinner size={14} /> : null}
      </div>
      <div className={styles.threadList} onScroll={handleListScroll}>
        {listError ? (
          <div className={styles.listError} role="alert">
            <span>
              {formatMessage(
                listError === 'load'
                  ? chatThreadListMessages.loadFailed
                  : chatThreadListMessages.archiveFailed,
              )}
            </span>
            <button
              type="button"
              className={styles.listErrorRetry}
              onClick={() => void loadThreads()}
            >
              {formatMessage(chatThreadListMessages.retry)}
            </button>
          </div>
        ) : null}
        {dateGroups.map((group) => (
          <div key={group.label} className={styles.dateGroup}>
            <span className={styles.threadGroupTitle}>{group.label}</span>
            {group.items.map(renderRow)}
          </div>
        ))}
        {!isLoadingThreads && !visible.length ? (
          <span className={styles.threadListEmpty}>
            {formatMessage(
              query
                ? chatThreadListMessages.nothingFound
                : chatThreadListMessages.noneYet,
            )}
          </span>
        ) : null}
      </div>
    </div>
  );
};
